import { CheckCircle2, ShieldCheck } from "lucide-react";
import { DateTime } from "luxon";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { TimezoneService } from "@/lib/timezone";

interface SharedSalon {
  salonId: string;
  salonName: string;
}

interface ConnectedCardProps {
  locationName: string;
  connectedAt: string;
  authMode: "OWNER_OAUTH" | "MANAGED_PLATFORM";
  managerEmail?: string | null;
  otherSalonsUsingSameAccount: SharedSalon[];
  // Location sync state — when null, the card hides the "Last synced" row.
  lastLocationSyncAt?: string | null;
  lastLocationSyncError?: string | null;
  onDisconnect: () => void;
  isLoading: boolean;
}

export function ConnectedCard({
  locationName,
  connectedAt,
  authMode,
  managerEmail,
  otherSalonsUsingSameAccount,
  lastLocationSyncAt,
  lastLocationSyncError,
  onDisconnect,
  isLoading,
}: ConnectedCardProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isManaged = authMode === "MANAGED_PLATFORM";
  const hasSharedSalons = otherSalonsUsingSameAccount.length > 0;

  const formattedDate = DateTime.fromISO(connectedAt, { zone: "utc" })
    .setZone(TimezoneService.getCurrentSalonTimezone())
    .toLocaleString({ year: "numeric", month: "short", day: "numeric" });

  const formattedLocationSync = lastLocationSyncAt
    ? DateTime.fromISO(lastLocationSyncAt, { zone: "utc" })
        .setZone(TimezoneService.getCurrentSalonTimezone())
        .toRelative()
    : null;

  const handleConfirm = () => {
    setConfirmOpen(false);
    onDisconnect();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div
            className={
              isManaged
                ? "rounded-md bg-amber-50 dark:bg-amber-950 p-2"
                : "rounded-md bg-emerald-50 dark:bg-emerald-950 p-2"
            }
          >
            {isManaged ? (
              <ShieldCheck className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            ) : (
              <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            )}
          </div>
          <div>
            <CardTitle>{isManaged ? "Managed by 96digital" : "Connected"}</CardTitle>
            <CardDescription>
              {isManaged
                ? "96digital syncs your Google Business reviews on your behalf."
                : "Your Google Business Profile is connected. Reviews sync weekly."}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Location</div>
          <div className="font-medium">{locationName}</div>
        </div>
        {isManaged && managerEmail ? (
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Managed by</div>
            <div className="font-mono text-sm">{managerEmail}</div>
          </div>
        ) : null}
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Connected on</div>
          <div className="text-sm">{formattedDate}</div>
        </div>
        {/* Location sync state — distinct from the weekly reviews sync. Only
            renders once the operator has triggered or scheduled the first
            location sync (lastLocationSyncAt is null until then). */}
        {formattedLocationSync ? (
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Address / hours last synced
            </div>
            <div className="text-sm">{formattedLocationSync}</div>
          </div>
        ) : null}
        {lastLocationSyncError ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
            Last sync failed: {lastLocationSyncError}
          </div>
        ) : null}
        <div className="flex flex-wrap gap-2 pt-2">
          {isManaged ? (
            <div className="text-sm text-muted-foreground">
              To disconnect, contact 96digital support. (You can also remove our manager email from
              your Google Business Profile users to stop sync immediately.)
            </div>
          ) : (
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(true)}
              disabled={isLoading}
              className="text-destructive hover:text-destructive"
            >
              {isLoading ? "Disconnecting..." : "Disconnect"}
            </Button>
          )}
        </div>
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Google Business?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Reviews will stop syncing from Google. Existing reviews already in your dashboard
                  will remain as read-only history. You can reconnect at any time.
                </p>
                {hasSharedSalons ? (
                  <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-foreground">
                    <p className="font-medium text-destructive">
                      Heads up: this will also break sync for{" "}
                      {otherSalonsUsingSameAccount.length === 1
                        ? "1 other salon"
                        : `${otherSalonsUsingSameAccount.length} other salons`}
                      .
                    </p>
                    <p className="mt-1">
                      Google revokes the entire grant when you disconnect, not just this salon.
                      Affected:
                    </p>
                    <ul className="mt-1 list-disc list-inside">
                      {otherSalonsUsingSameAccount.map((salon) => (
                        <li key={salon.salonId}>{salon.salonName}</li>
                      ))}
                    </ul>
                    <p className="mt-1">
                      Those salons will need to reconnect their Google Business Profile.
                    </p>
                  </div>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className="bg-destructive hover:bg-destructive/90"
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
